import React from 'react'

import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    Grid,
} from '@material-ui/core'
import { ArrowBack as ArrowLeft, Send } from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import {
    CampaignData,
    ProspectList,
    SequenceItem,
    SequenceStep,
    DelayStep,
} from './types'

const useStyles = makeStyles((theme: Theme) => ({
    cardTitle: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#1f2937',
    },
    cardDescription: {
        color: '#6b7280',
        fontSize: '0.875rem',
        marginTop: theme.spacing(0.5),
    },
    summaryCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    summaryItem: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '0.875rem',
        marginBottom: theme.spacing(1),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    navigationContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
    },
    gradientButton: {
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        color: 'white',
        borderRadius: '8px',
        '&:hover': {
            boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
    },
}))

interface CampaignLaunchTabProps {
    campaignData: CampaignData
    sequence: SequenceItem[]
    onBack: () => void
    onCreateCampaign: () => void
    isCreating?: boolean
}

const isMessageStep = (item: SequenceItem): item is SequenceStep => {
    return item.type === 'email' || item.type === 'sms'
}

const isDelayStep = (item: SequenceItem): item is DelayStep => {
    return item.type === 'delay'
}

export const CampaignLaunchTab: React.FC<CampaignLaunchTabProps> = ({
    campaignData,
    sequence,
    onBack,
    onCreateCampaign,
    isCreating = false,
}) => {
    const classes = useStyles()
    const messageSteps = sequence.filter(isMessageStep)
    const delaySteps = sequence.filter(isDelayStep)
    const totalDays = delaySteps.reduce((sum, delay) => sum + delay.days, 0)

    return (
        <Box
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 24,
                width: '100%',
            }}
        >
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card className={classes.summaryCard}>
                        <Box style={{ padding: '24px 24px 16px 24px' }}>
                            <Typography className={classes.cardTitle}>
                                Campaign Review
                            </Typography>
                            <Typography className={classes.cardDescription}>
                                Review your campaign before launching
                            </Typography>
                        </Box>
                        <CardContent style={{ padding: '0 24px 24px 24px' }}>
                            <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 12,
                                }}
                            >
                                <Box className={classes.summaryItem}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Campaign Name:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 500 }}
                                    >
                                        {campaignData.name}
                                    </Typography>
                                </Box>
                                <Box className={classes.summaryItem}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Sequence Steps:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 500 }}
                                    >
                                        {messageSteps.length}
                                    </Typography>
                                </Box>
                                <Box className={classes.summaryItem}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Estimated Duration:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 500 }}
                                    >
                                        {totalDays}{' '}
                                        {totalDays === 1 ? 'day' : 'days'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card className={classes.summaryCard}>
                        <Box style={{ padding: '24px 24px 16px 24px' }}>
                            <Typography className={classes.cardTitle}>
                                Launch Options
                            </Typography>
                        </Box>
                        <CardContent style={{ padding: '0 24px 24px 24px' }}>
                            <Button
                                fullWidth
                                size="large"
                                variant="contained"
                                color="primary"
                                startIcon={<Send />}
                                className={classes.gradientButton}
                                onClick={onCreateCampaign}
                                disabled={isCreating}
                            >
                                {isCreating
                                    ? 'Creating Campaign...'
                                    : 'Launch Campaign Now'}
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Box className={classes.navigationContainer}>
                <Button
                    variant="outlined"
                    onClick={onBack}
                    startIcon={<ArrowLeft />}
                >
                    Back to Sequence
                </Button>
            </Box>
        </Box>
    )
}
